 
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable, ElementRef, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { EventBusService } from '../../client/event-bus.service';
import {
  IChatUIService,
  ScrollState,
  NotificationOptions,
  NotificationState,
} from '../interfaces/chat-ui-service.interface';
import { ChatScrollService } from './chat-scroll.service';
import { ChatNotificationService } from './chat-notification.service';
import { FileUploadService } from './file-upload.service';
import { FileUpload } from '@app-libs/model';

/**
 * Unified service for chat UI operations including notifications
 * and UI helper functions. Scroll management is delegated to ChatScrollService,
 * and notification management is delegated to ChatNotificationService.
 */
@Injectable({
  providedIn: 'root',
})
export class ChatUIService implements IChatUIService, OnDestroy {
  // ===== Public Observables =====
  // Delegate notification observables to ChatNotificationService
  public readonly currentNotification$: Observable<NotificationState | null>;

  // Delegate scroll observables to ChatScrollService
  public readonly scrollState$: Observable<ScrollState>;
  public readonly showScrollButton$: Observable<boolean>;
  public readonly autoScroll$: Observable<boolean>;
  public readonly isAtBottom$: Observable<boolean>;
  
  // Delegate file upload observables to FileUploadService
  public readonly uploadedFile$: Observable<FileUpload | undefined>;
  public readonly isUploading$: Observable<boolean>;

  constructor(
    private eventBus: EventBusService,
    private chatScrollService: ChatScrollService,
    private chatNotificationService: ChatNotificationService,
    private fileUploadService: FileUploadService,
  ) {
    // Expose ChatNotificationService observables through this service
    this.currentNotification$ = this.chatNotificationService.currentNotification$;
    
    // Expose ChatScrollService observables through this service
    this.scrollState$ = this.chatScrollService.scrollState$;
    this.showScrollButton$ = this.chatScrollService.showScrollButton$;
    this.autoScroll$ = this.chatScrollService.autoScroll$;
    this.isAtBottom$ = this.chatScrollService.isAtBottom$;
    
    // Expose FileUploadService observables through this service
    this.uploadedFile$ = this.fileUploadService.uploadedFile$;
    this.isUploading$ = this.fileUploadService.isUploading$;
  }

  // ===== Scroll Management Methods =====
  // All scroll methods delegate to ChatScrollService

  /**
   * Initializes scroll monitoring for a container element
   * Delegates to ChatScrollService
   */
  initScrollMonitoring(container: ElementRef<HTMLElement>): void {
    console.debug('[ChatUIService] Delegating initScrollMonitoring to ChatScrollService');
    this.chatScrollService.initScrollMonitoring(container);
  }

  /**
   * Scrolls to bottom using current scroll container (interface compatibility)
   * Delegates to ChatScrollService
   */
  scrollToBottom(): void {
    console.debug('[ChatUIService] Delegating scrollToBottom to ChatScrollService');
    this.chatScrollService.scrollToBottom();
  }

  /**
   * Scrolls to the bottom of the given element
   * Delegates to ChatScrollService
   */
  scrollToBottomWithElement(element: ElementRef, smooth = true): void {
    console.debug('[ChatUIService] Delegating scrollToBottomWithElement to ChatScrollService');
    // ChatScrollService uses the container set during initScrollMonitoring
    // so we just call scrollToBottom with the smooth parameter
    this.chatScrollService.scrollToBottom(smooth);
  }

  /**
   * Toggles auto-scroll state
   * Delegates to ChatScrollService
   */
  toggleAutoScroll(): void {
    const current = this.chatScrollService.getScrollState().autoScroll;
    this.setAutoScroll(!current);
  }

  /**
   * Sets the auto-scroll state
   * Delegates to ChatScrollService
   */
  setAutoScroll(enabled: boolean): void {
    console.debug('[ChatUIService] Delegating setAutoScroll to ChatScrollService', { enabled });
    this.chatScrollService.setAutoScroll(enabled);
  }

  /**
   * Scrolls to the bottom with a delay
   * Delegates to ChatScrollService
   */
  scrollToBottomDelayed(element: ElementRef, delay = 100, smooth = true): void {
    // console.debug('[ChatUIService] Delegating scrollToBottomDelayed to ChatScrollService');
    // ChatScrollService doesn't need the element parameter as it uses
    // the container set during initScrollMonitoring
    this.chatScrollService.scrollToBottomDelayed(delay);
  }

  /**
   * Handles new content being added (e.g., new messages)
   * Delegates to ChatScrollService
   */
  handleNewContent(): void {
    // console.debug('[ChatUIService] Delegating handleNewContent to ChatScrollService');
    this.chatScrollService.handleNewContent();
  }

  /**
   * Gets current scroll state snapshot
   * Delegates to ChatScrollService
   */
  getScrollState(): ScrollState {
    return this.chatScrollService.getScrollState();
  }

  /**
   * Resets scroll state to defaults
   * Delegates to ChatScrollService
   */
  resetScrollState(): void {
    console.debug('[ChatUIService] Delegating resetScrollState to ChatScrollService');
    this.chatScrollService.resetScrollState();
  }

  // ===== Notification Methods =====
  // All notification methods delegate to ChatNotificationService

  /**
   * Shows a success notification
   * Delegates to ChatNotificationService
   */
  showSuccess(message: string, options?: NotificationOptions): MatSnackBarRef<SimpleSnackBar> {
    return this.chatNotificationService.showSuccess(message, options);
  }

  /**
   * Shows an error notification
   * Delegates to ChatNotificationService
   */
  showError(message: string, options?: NotificationOptions): MatSnackBarRef<SimpleSnackBar> {
    return this.chatNotificationService.showError(message, options);
  }

  /**
   * Shows a warning notification
   * Delegates to ChatNotificationService
   */
  showWarning(message: string, options?: NotificationOptions): MatSnackBarRef<SimpleSnackBar> {
    return this.chatNotificationService.showWarning(message, options);
  }

  /**
   * Shows an info notification
   * Delegates to ChatNotificationService
   */
  showInfo(message: string, options?: NotificationOptions): MatSnackBarRef<SimpleSnackBar> {
    return this.chatNotificationService.showInfo(message, options);
  }

  /**
   * Dismisses all active notifications
   * Delegates to ChatNotificationService
   */
  dismissAllNotifications(): void {
    this.chatNotificationService.dismissAllNotifications();
  }

  // ===== Pre-built Notifications =====
  // All pre-built notifications delegate to ChatNotificationService

  /**
   * Notifies when file size exceeds limit
   * Delegates to ChatNotificationService
   */
  notifyFileTooLarge(maxSize: number): void {
    this.chatNotificationService.notifyFileTooLarge(maxSize);
  }

  /**
   * Notifies when file type is not supported
   * Delegates to ChatNotificationService
   */
  notifyUnsupportedFileType(fileType: string): void {
    this.chatNotificationService.notifyUnsupportedFileType(fileType);
  }

  /**
   * Notifies successful file upload
   * Delegates to ChatNotificationService
   */
  notifyUploadSuccess(fileName: string): void {
    this.chatNotificationService.notifyUploadSuccess(fileName);
  }

  /**
   * Notifies upload error
   * Delegates to ChatNotificationService
   */
  notifyUploadError(error: string): void {
    this.chatNotificationService.notifyUploadError(error);
  }

  /**
   * Notifies successful copy to clipboard
   * Delegates to ChatNotificationService
   */
  notifyCopySuccess(): void {
    this.chatNotificationService.notifyCopySuccess();
  }

  /**
   * Notifies connection error
   * Delegates to ChatNotificationService
   */
  notifyConnectionError(): void {
    this.chatNotificationService.notifyConnectionError();
  }

  /**
   * Notifies rate limit exceeded
   * Delegates to ChatNotificationService
   */
  notifyRateLimitExceeded(): void {
    this.chatNotificationService.notifyRateLimitExceeded();
  }

  // ===== Specific Notification Methods =====
  // All specific notifications delegate to ChatNotificationService

  /**
   * Shows a notification for file upload error with details
   * Delegates to ChatNotificationService
   */
  showFileUploadError(fileName: string, error: string): MatSnackBarRef<SimpleSnackBar> {
    return this.chatNotificationService.showFileUploadError(fileName, error);
  }

  /**
   * Shows a notification for message copy error
   * Delegates to ChatNotificationService
   */
  showMessageCopyError(): MatSnackBarRef<SimpleSnackBar> {
    return this.chatNotificationService.showMessageCopyError();
  }

  /**
   * Shows a notification for API errors
   * Delegates to ChatNotificationService
   */
  showApiError(error: any): MatSnackBarRef<SimpleSnackBar> {
    return this.chatNotificationService.showApiError(error);
  }

  /**
   * Shows a notification for authentication errors
   * Delegates to ChatNotificationService
   */
  showAuthError(): MatSnackBarRef<SimpleSnackBar> {
    return this.chatNotificationService.showAuthError();
  }

  /**
   * Shows a notification for unsupported file types with details
   * Delegates to ChatNotificationService
   */
  showUnsupportedFileError(
    fileName: string,
    supportedTypes: string[],
  ): MatSnackBarRef<SimpleSnackBar> {
    return this.chatNotificationService.showUnsupportedFileError(fileName, supportedTypes);
  }

  /**
   * Shows a notification for file size errors
   * Delegates to ChatNotificationService
   */
  showFileSizeError(fileName: string, maxSize: string): MatSnackBarRef<SimpleSnackBar> {
    return this.chatNotificationService.showFileSizeError(fileName, maxSize);
  }

  /**
   * Dismisses a specific notification
   * Delegates to ChatNotificationService
   */
  dismiss(notificationRef: MatSnackBarRef<SimpleSnackBar>): void {
    this.chatNotificationService.dismiss(notificationRef);
  }

  /**
   * Gets the current notification state
   * Delegates to ChatNotificationService
   */
  getCurrentNotification(): NotificationState | null {
    return this.chatNotificationService.getCurrentNotification();
  }

  // ===== UI Helper Methods =====

  /**
   * Formats file name for display with optional truncation
   */
  formatFileDisplay(fileName: string, maxLength = 30): string {
    if (fileName.length <= maxLength) {
      return fileName;
    }

    const extension =
      fileName.lastIndexOf('.') > -1 ? fileName.substring(fileName.lastIndexOf('.')) : '';

    const nameWithoutExt = extension ? fileName.substring(0, fileName.lastIndexOf('.')) : fileName;

    const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 3);

    return `${truncatedName}...${extension}`;
  }

  // ===== File Upload Delegation Methods =====
  
  /**
   * Handles file upload through the FileUploadService
   * This includes OpenAI backend upload logic when needed
   */
  async handleFileUpload(file: File): Promise<void> {
    console.debug('[ChatUIService] Delegating file upload to FileUploadService');
    await this.fileUploadService.handleFile(file);
  }

  /**
   * Gets the current uploaded file
   */
  get uploadedFile(): FileUpload | undefined {
    return this.fileUploadService.uploadedFile;
  }

  /**
   * Clears the uploaded file
   */
  clearUploadedFile(): void {
    this.fileUploadService.clearUploadFile();
  }

  /**
   * Removes the uploaded file
   */
  removeUploadedFile(): void {
    this.fileUploadService.removeUploadedFile();
  }

  // ===== Lifecycle =====

  ngOnDestroy(): void {
    // Clean up scroll service resources
    this.chatScrollService.destroy();

    // Notification cleanup is handled by ChatNotificationService
  }

}
